import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import EvaluationLayout from '../../components/EvaluationLayout';
import evaluationService from '../../services/evaluationService';
import {
  Calendar,
  Clock,
  Settings,
  Webhook,
  Play,
  Pause,
  CheckCircle,
  XCircle,
  RefreshCw,
  Globe,
  Save,
  X
} from 'lucide-react';
import Button from '../../components/ui/button';
import Input from '../../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import Badge from '../../components/ui/badge';
import { formatDate } from '../../lib/utils';
import { toast } from 'react-hot-toast';

const SchedulingInterface = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    frequency: 'daily',
    time: '09:00',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    enabled: true,
    day_of_week: 1, // Monday
    day_of_month: 1
  });
  const [webhookForm, setWebhookForm] = useState({
    url: '',
    enabled: true
  });
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [webhookTestResult, setWebhookTestResult] = useState(null);

  const loadScheduledEvaluations = useCallback(async () => {
    try {
      setLoading(true);

      if (!user) {
        console.error('User not authenticated');
        toast.error('Please log in to view scheduled evaluations');
        setLoading(false);
        return;
      }

      // Disable cache for schedules endpoint to always get fresh data
      const response = await evaluationService.getScheduledEvaluations({}, { cache: false });

      if (!response.success) {
        throw new Error(response.error || 'Failed to load schedules');
      }

      console.log('Loaded scheduled evaluations:', response);
      setEvaluations(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load scheduled evaluations:', error);
      toast.error(error.message || 'Failed to load scheduled evaluations');
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading && user) {
      loadScheduledEvaluations();
    }
  }, [authLoading, user, loadScheduledEvaluations]);

  // Refresh data when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user) {
        loadScheduledEvaluations();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, loadScheduledEvaluations]);

  const handleScheduleEvaluation = (evaluation) => {
    setSelectedEvaluation(evaluation);
    setScheduleForm({
      frequency: evaluation.schedule?.frequency || 'daily',
      time: evaluation.schedule?.time || '09:00',
      timezone: evaluation.schedule?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      enabled: evaluation.schedule?.enabled ?? true,
      day_of_week: evaluation.schedule?.day_of_week || 1,
      day_of_month: evaluation.schedule?.day_of_month || 1
    });
    setShowScheduleModal(true);
  };

  const handleWebhookConfiguration = (evaluation) => {
    setSelectedEvaluation(evaluation);
    setWebhookForm({
      url: evaluation.webhook_url || '',
      enabled: !!evaluation.webhook_url
    });
    setWebhookTestResult(null);
    setShowWebhookModal(true);
  };

  const handleSaveSchedule = async () => {
    try {
      const response = await evaluationService.scheduleEvaluation(selectedEvaluation.id, scheduleForm);

      if (!response.success) {
        throw new Error(response.error || 'Failed to save schedule');
      }

      // Reload evaluations to get updated data
      await loadScheduledEvaluations();

      setShowScheduleModal(false);
      setSelectedEvaluation(null);

      toast.success('Schedule saved successfully!');
    } catch (error) {
      console.error('Failed to save schedule:', error);
      toast.error(error.message || 'Failed to save schedule');
    }
  };

  const handleSaveWebhook = async () => {
    try {
      const response = await evaluationService.updateEvaluation(selectedEvaluation.id, {
        webhook_url: webhookForm.enabled ? webhookForm.url : null
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to save webhook');
      }

      // Reload evaluations to get updated data
      await loadScheduledEvaluations();

      setShowWebhookModal(false);
      setSelectedEvaluation(null);

      toast.success('Webhook configuration saved successfully!');
    } catch (error) {
      console.error('Failed to save webhook:', error);
      toast.error(error.message || 'Failed to save webhook configuration');
    }
  };

  const handleTestWebhook = async () => {
    if (!webhookForm.url) return;

    try {
      setTestingWebhook(true);
      // TODO: Replace with actual API call
      // const response = await axios.post('/api/webhooks/test', { url: webhookForm.url });
      // setWebhookTestResult(response.data);

      // Mock implementation
      setTimeout(() => {
        setWebhookTestResult({
          success: true,
          message: 'Webhook test successful',
          response_time: 245
        });
        setTestingWebhook(false);
      }, 2000);
    } catch (error) {
      setWebhookTestResult({
        success: false,
        message: 'Webhook test failed: ' + error.message
      });
      setTestingWebhook(false);
    }
  };

  const handleToggleSchedule = async (evaluationId, enabled) => {
    try {
      const response = enabled
        ? await evaluationService.enableSchedule(evaluationId)
        : await evaluationService.disableSchedule(evaluationId);

      if (!response.success) {
        throw new Error(response.error || 'Failed to toggle schedule');
      }

      // Reload evaluations to get updated data
      await loadScheduledEvaluations();

      toast.success(`Schedule ${enabled ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      console.error('Failed to toggle schedule:', error);
      toast.error('Failed to toggle schedule');
    }
  };

  const getStatusIcon = (evaluation) => {
    if (!evaluation.schedule?.enabled) {
      return <Pause className="w-4 h-4 text-gray-500" />;
    }

    switch (evaluation.status) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'paused':
        return <Pause className="w-4 h-4 text-yellow-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-blue-500" />;
    }
  };

  const getStatusBadge = (evaluation) => {
    if (!evaluation.schedule?.enabled) {
      return <Badge variant="outline">Disabled</Badge>;
    }

    const variants = {
      active: 'secondary',
      paused: 'outline',
      failed: 'destructive'
    };

    return (
      <Badge variant={variants[evaluation.status] || 'outline'}>
        {evaluation.status === 'active' ? 'Scheduled' : evaluation.status.charAt(0).toUpperCase() + evaluation.status.slice(1)}
      </Badge>
    );
  };

  const getFrequencyDisplay = (schedule) => {
    if (!schedule || !schedule.frequency) return 'Not scheduled';

    const freq = String(schedule.frequency);
    let display = freq.charAt(0).toUpperCase() + freq.slice(1);

    if (freq === 'weekly' && schedule.day_of_week !== undefined) {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      display += ` (${days[schedule.day_of_week]})`;
    } else if (freq === 'monthly' && schedule.day_of_month !== undefined) {
      display += ` (${schedule.day_of_month}${getOrdinalSuffix(schedule.day_of_month)})`;
    }

    return display;
  };

  const getOrdinalSuffix = (num) => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  };

  const ScheduleModal = () => {
    if (!showScheduleModal || !selectedEvaluation) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-card border border-border rounded-lg max-w-lg w-full">
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-foreground">Schedule Configuration</h2>
              <Button variant="ghost" size="icon" onClick={() => setShowScheduleModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{selectedEvaluation.name}</p>
          </div>

          <div className="p-6 space-y-4">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Enable Schedule</label>
              <button
                type="button"
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${scheduleForm.enabled ? 'bg-primary' : 'bg-gray-200'
                  }`}
                onClick={() => setScheduleForm(prev => ({ ...prev, enabled: !prev.enabled }))}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${scheduleForm.enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                />
              </button>
            </div>

            {scheduleForm.enabled && (
              <>
                {/* Frequency */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Frequency</label>
                  <Select value={scheduleForm.frequency} onValueChange={(value) => setScheduleForm(prev => ({ ...prev, frequency: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Day of Week (for weekly) */}
                {scheduleForm.frequency === 'weekly' && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">Day of Week</label>
                    <Select value={scheduleForm.day_of_week.toString()} onValueChange={(value) => setScheduleForm(prev => ({ ...prev, day_of_week: parseInt(value) }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Sunday</SelectItem>
                        <SelectItem value="1">Monday</SelectItem>
                        <SelectItem value="2">Tuesday</SelectItem>
                        <SelectItem value="3">Wednesday</SelectItem>
                        <SelectItem value="4">Thursday</SelectItem>
                        <SelectItem value="5">Friday</SelectItem>
                        <SelectItem value="6">Saturday</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Day of Month (for monthly) */}
                {scheduleForm.frequency === 'monthly' && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">Day of Month</label>
                    <Input
                      type="number"
                      min="1"
                      max="31"
                      value={scheduleForm.day_of_month}
                      onChange={(e) => setScheduleForm(prev => ({ ...prev, day_of_month: parseInt(e.target.value) || 1 }))}
                    />
                  </div>
                )}

                {/* Time */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Time</label>
                  <Input
                    type="time"
                    value={scheduleForm.time}
                    onChange={(e) => setScheduleForm(prev => ({ ...prev, time: e.target.value }))}
                  />
                </div>

                {/* Timezone */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Timezone</label>
                  <Input
                    type="text"
                    value={scheduleForm.timezone}
                    onChange={(e) => setScheduleForm(prev => ({ ...prev, timezone: e.target.value }))}
                    placeholder="UTC"
                  />
                </div>
              </>
            )}
          </div>

          <div className="p-6 border-t border-border flex justify-end space-x-3">
            <Button variant="outline" onClick={() => setShowScheduleModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveSchedule}>
              <Save className="w-4 h-4 mr-2" />
              Save Schedule
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const WebhookModal = () => {
    if (!showWebhookModal || !selectedEvaluation) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-card border border-border rounded-lg max-w-lg w-full">
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-foreground">Webhook Configuration</h2>
              <Button variant="ghost" size="icon" onClick={() => setShowWebhookModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{selectedEvaluation.name}</p>
          </div>

          <div className="p-6 space-y-4">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Enable Webhook</label>
              <button
                type="button"
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${webhookForm.enabled ? 'bg-primary' : 'bg-gray-200'
                  }`}
                onClick={() => setWebhookForm(prev => ({ ...prev, enabled: !prev.enabled }))}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${webhookForm.enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                />
              </button>
            </div>

            {webhookForm.enabled && (
              <>
                {/* Webhook URL */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Webhook URL</label>
                  <Input
                    type="url"
                    value={webhookForm.url}
                    onChange={(e) => setWebhookForm(prev => ({ ...prev, url: e.target.value }))}
                    placeholder="https://api.example.com/webhooks/evaluation"
                  />
                </div>

                {/* Test Webhook */}
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleTestWebhook}
                    disabled={!webhookForm.url || testingWebhook}
                  >
                    {testingWebhook ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Globe className="w-4 h-4 mr-2" />
                    )}
                    Test Webhook
                  </Button>
                </div>

                {/* Test Result */}
                {webhookTestResult && (
                  <div className={`p-3 rounded-lg border ${webhookTestResult.success
                    ? 'bg-green-50 border-green-200 text-green-800'
                    : 'bg-red-50 border-red-200 text-red-800'
                    }`}>
                    <div className="flex items-center space-x-2">
                      {webhookTestResult.success ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : (
                        <XCircle className="w-4 h-4" />
                      )}
                      <span className="text-sm font-medium">{webhookTestResult.message}</span>
                    </div>
                    {webhookTestResult.response_time && (
                      <p className="text-xs mt-1">Response time: {webhookTestResult.response_time}ms</p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="p-6 border-t border-border flex justify-end space-x-3">
            <Button variant="outline" onClick={() => setShowWebhookModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveWebhook}>
              <Save className="w-4 h-4 mr-2" />
              Save Webhook
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Show loading while authenticating
  if (authLoading) {
    return (
      <EvaluationLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      </EvaluationLayout>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return (
      <EvaluationLayout>
        <div className="text-center py-12">
          <XCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Authentication Required</h3>
          <p className="text-muted-foreground mb-4">Please log in to view scheduled evaluations.</p>
          <Button onClick={() => navigate('/login')}>
            Go to Login
          </Button>
        </div>
      </EvaluationLayout>
    );
  }

  return (
    <EvaluationLayout>
      <div className="space-y-8 w-full">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Evaluation Scheduling</h1>
            <p className="text-muted-foreground mt-1">
              Manage automated evaluation schedules and webhook notifications
            </p>
          </div>
          <div className="flex space-x-3">
            <Button variant="outline" onClick={loadScheduledEvaluations}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>

          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card
            role="button"
            tabIndex={0}
            onClick={() => navigate('/evaluations?status=scheduled')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/evaluations?status=scheduled'); } }}
            className="cursor-pointer hover:bg-accent/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
          >
            <CardContent className="p-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Calendar className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Scheduled</p>
                  <p className="text-2xl font-bold text-foreground">
                    {evaluations.filter(e => e.schedule).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card
            role="button"
            tabIndex={0}
            onClick={() => navigate('/evaluations?status=scheduled')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/evaluations?status=scheduled'); } }}
            className="cursor-pointer hover:bg-accent/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
          >
            <CardContent className="p-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-500/10 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Active</p>
                  <p className="text-2xl font-bold text-foreground">
                    {evaluations.filter(e => e.schedule?.enabled).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card
            role="button"
            tabIndex={0}
            onClick={() => navigate('/evaluations/webhooks')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/evaluations/webhooks'); } }}
            className="cursor-pointer hover:bg-accent/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
          >
            <CardContent className="p-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <Webhook className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">With Webhooks</p>
                  <p className="text-2xl font-bold text-foreground">
                    {evaluations.filter(e => e.webhook_url).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card
            role="button"
            tabIndex={0}
            onClick={() => navigate('/evaluations?status=scheduled')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/evaluations?status=scheduled'); } }}
            className="cursor-pointer hover:bg-accent/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
          >
            <CardContent className="p-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-yellow-500/10 rounded-lg">
                  <Clock className="w-6 h-6 text-yellow-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Next Run</p>
                  <p className="text-sm font-bold text-foreground">
                    {evaluations.find(e => e.next_run && e.schedule?.enabled)?.next_run
                      ? formatDate(evaluations.find(e => e.next_run && e.schedule?.enabled).next_run)
                      : 'None scheduled'
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Scheduled Evaluations List */}
        <Card>
          <CardHeader>
            <CardTitle>Scheduled Evaluations</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Loading scheduled evaluations...</p>
              </div>
            ) : evaluations.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">No scheduled evaluations</h3>
                <p className="text-muted-foreground mb-4">
                  Scheduled evaluations will appear here when available
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {evaluations.map((evaluation) => (
                  <div
                    key={evaluation.id}
                    className="border border-border rounded-lg p-4 hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          {getStatusIcon(evaluation)}
                          <h3 className="text-lg font-medium text-foreground">
                            {evaluation.name}
                          </h3>
                          {getStatusBadge(evaluation)}
                        </div>

                        <p className="text-sm text-muted-foreground mb-3">
                          {evaluation.description}
                        </p>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">Provider</p>
                            <p className="font-medium">{evaluation.provider}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Frequency</p>
                            <p className="font-medium">{getFrequencyDisplay(evaluation.schedule)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Next Run</p>
                            <p className="font-medium">
                              {evaluation.next_run ? formatDate(evaluation.next_run) : 'Not scheduled'}
                            </p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Webhook</p>
                            <p className="font-medium">
                              {evaluation.webhook_url ? 'Configured' : 'Not configured'}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        {/* Toggle Schedule */}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleToggleSchedule(evaluation.id, !evaluation.schedule?.enabled)}
                          title={evaluation.schedule?.enabled ? 'Disable schedule' : 'Enable schedule'}
                        >
                          {evaluation.schedule?.enabled ? (
                            <Pause className="w-4 h-4" />
                          ) : (
                            <Play className="w-4 h-4" />
                          )}
                        </Button>

                        {/* Configure Schedule */}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleScheduleEvaluation(evaluation)}
                          title="Configure schedule"
                        >
                          <Settings className="w-4 h-4" />
                        </Button>

                        {/* Configure Webhook */}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleWebhookConfiguration(evaluation)}
                          title="Configure webhook"
                        >
                          <Webhook className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modals */}
        <ScheduleModal />
        <WebhookModal />
      </div>
    </EvaluationLayout>
  );
};

export default SchedulingInterface;
